import { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { Card } from '../ui';

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

interface LineProps {
  labels: string[];
  values: number[];
  height?: number;
}

/** Single-series margin line — rust stroke, 12% area fill. */
function MarginLine({ labels, values, height = 140 }: LineProps) {
  const w = 600;
  const h = height;
  const pad = 6;
  const hi = Math.max(...values);
  const lo = Math.min(...values);
  const spread = hi - lo || 1;
  const max = hi + spread * 0.15;
  const min = lo - spread * 0.15;
  const x = (i: number) => pad + (i / Math.max(values.length - 1, 1)) * (w - pad * 2);
  const y = (v: number) => h - pad - ((v - min) / (max - min)) * (h - pad * 2);
  const d = values.map((v, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)} ${y(v).toFixed(1)}`).join(' ');
  const area = `${d} L${x(values.length - 1).toFixed(1)} ${h - pad} L${x(0).toFixed(1)} ${h - pad} Z`;

  return (
    <div>
      <svg
        viewBox={`0 0 ${w} ${h}`}
        style={{ width: '100%', height: 'auto', display: 'block' }}
        preserveAspectRatio="none"
      >
        <path d={area} fill="var(--chart-1)" opacity="0.12" />
        <path d={d} fill="none" stroke="var(--chart-1)" strokeWidth="2" vectorEffect="non-scaling-stroke" />
        {values.length > 0 && (
          <circle cx={x(values.length - 1)} cy={y(values[values.length - 1])} r="3.5" fill="var(--chart-1)" />
        )}
      </svg>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '6px' }}>
        {labels.map((lab, i) => (
          <span key={lab + i} className="t-num" style={{ fontSize: '10px', color: 'var(--text-faint)' }}>
            {lab}
          </span>
        ))}
      </div>
    </div>
  );
}

export default function MarginChart() {
  const [labels, setLabels] = useState<string[]>([]);
  const [values, setValues] = useState<number[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const fetchMarginData = async () => {
      try {
        const [expensesResult, transactionsResult] = await Promise.all([
          supabase
            .from('financial_expenses')
            .select('month, year, amount')
            .order('year', { ascending: false })
            .order('month', { ascending: false }),
          supabase.from('stripe_transactions').select('created_at, amount').gt('amount', 0),
        ]);

        if (expensesResult.error) throw expensesResult.error;
        if (transactionsResult.error) throw transactionsResult.error;
        if (cancelled) return;

        const expensesByMonth = new Map<string, number>();
        expensesResult.data?.forEach((exp) => {
          expensesByMonth.set(`${exp.year}-${exp.month}`, Number(exp.amount));
        });

        const revenueByMonth = new Map<string, number>();
        transactionsResult.data?.forEach((txn) => {
          const date = new Date(txn.created_at);
          const key = `${date.getFullYear()}-${date.getMonth() + 1}`;
          revenueByMonth.set(key, (revenueByMonth.get(key) || 0) + txn.amount / 100);
        });

        // Trailing 12 months of margin %
        const now = new Date();
        const monthLabels: string[] = [];
        const margins: number[] = [];
        for (let i = 11; i >= 0; i--) {
          const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
          const key = `${d.getFullYear()}-${d.getMonth() + 1}`;
          const revenue = revenueByMonth.get(key) || 0;
          const expenses = expensesByMonth.get(key) || 0;
          const margin = revenue > 0 ? ((revenue - expenses) / revenue) * 100 : 0;
          monthLabels.push(MONTH_NAMES[d.getMonth()]);
          margins.push(Math.round(margin));
        }

        setLabels(monthLabels);
        setValues(margins);
      } catch (err) {
        console.error('Error fetching margin data:', err);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    fetchMarginData();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <Card eyebrow="Margin Trend" title="Trailing 12 months">
      {isLoading ? (
        <div
          style={{
            height: '140px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 'var(--text-sm)',
            color: 'var(--text-faint)',
          }}
        >
          Loading...
        </div>
      ) : (
        <MarginLine labels={labels} values={values} />
      )}
    </Card>
  );
}
